import React, { FC, RefObject, useEffect, useState } from 'react';
import { StatusBar } from 'react-native';
import { ImageDocumentStoreItem, ImageDocumentStoreItemWithAngle } from '@v2/components/imageHandling/types/types.d';
import { ColorFunction } from '@styles/colors/types';
import { isAndroid } from '@helpers';
import { CropView } from 'react-native-image-crop-tools';
import { AndroidCropper } from './AndroidCropper';
import { IOSCropper, IOSCropperProps } from './IOSCropper';
import ReactNativeBlobUtil from 'react-native-blob-util';
import DeviceInfo from 'react-native-device-info';
import RNFS from 'react-native-fs';
export enum CropMode {
  SAVE = 'Save',
  CANCEL = 'Cancel',
}
export interface CropperTabletMobileProps {
  image: ImageDocumentStoreItem;
  cropRef: RefObject<CropView>;
  angle: number;
  onSave: (image: ImageDocumentStoreItemWithAngle) => void;
  triggerShow: () => void;
  C: ColorFunction;
  modeRef: React.MutableRefObject<CropMode>;
  triggerDiscardChangesPopup: () => void;
  setShowChooseCropModeModalOpen?: (value: boolean) => void;
  setPendingCropData?: (data: any) => void;
  isNewImage?: boolean;
}
const CropperTabletMobile: FC<CropperTabletMobileProps> = (props) => {
  const initialBase64Original = props.image?.base64Original;
  const [base64Original, setBase64Original] = useState<string | null>(initialBase64Original ?? null);
  useEffect(() => {
    StatusBar.setHidden(true);
    if (initialBase64Original) {
      return;
    }
    (async () => {
      try {
        if (!props.image.originUrl) {
          console.error('Origin URL is not available');
          return;
        }
        if (isAndroid) {
          const isSamsung = DeviceInfo.getBrand().toLowerCase() === 'samsung';
          ReactNativeBlobUtil.fetch('GET', props.image.originUrl, {
            // @ts-ignore
            ignoreInterceptor: 'true'
          })
          .then((res) => res.base64())
          .then(async (base64) => {
            if (isSamsung) {
              // Samsung: Use file URI instead of base64 (workaround for known CropImageView bug)
              // See: https://github.com/CanHub/Android-Image-Cropper/issues/599
              const tempPath = `${RNFS.CachesDirectoryPath}/crop_temp_${Date.now()}.png`;
              await RNFS.writeFile(tempPath, base64, 'base64');
              const fileUri = `file://${tempPath}`;
              setBase64Original(fileUri);
            } else {
              // Other Android devices: Use base64 data URI
              const base64String = `data:image/jpeg;base64,${base64}`;
              setBase64Original(base64String);
            }
          })
          .catch((error) => {
            console.error('Error fetching image with ReactNativeBlobUtil:', error);
          });
        } else {
          // For iOS, use fetch as before
          const response = await fetch(props.image.originUrl, { ignoreInterceptor: 'true' });
          if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status}`);
          }
          const blob = await response.blob();
          // Convert blob to base64
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            setBase64Original(base64String);
          };
          reader.readAsDataURL(blob);
        }
      } catch (error) {
        console.error('Error fetching image:', error);
      }
    })();
    return () => {
      StatusBar.setHidden(false);
    };
  }, [initialBase64Original]);
  const cropperProps: IOSCropperProps = {
    ...props,
    base64Original: base64Original,
  };
  return isAndroid ? <AndroidCropper {...cropperProps} /> : <IOSCropper {...cropperProps} />;
};
export default CropperTabletMobile;